/**
 * Clipboard Utilities
 * 
 * Provides functions for copying text to the clipboard with proper
 * error handling and user feedback via toast notifications.
 */

import { butterToast } from '#patterns/Toast'

/**
 * Copy text to clipboard with toast feedback
 * 
 * Uses the modern Clipboard API to copy text and provides user feedback
 * via toast notifications. Handles errors gracefully and falls back to
 * showing an error toast if the operation fails.
 * 
 * @param text - The text to copy to clipboard
 * @param successMessage - Optional custom success message. Defaults to "Copied to clipboard"
 * @param errorMessage - Optional custom error message. Defaults to "Failed to copy to clipboard"
 * @returns Promise that resolves when copy is complete or rejects on error
 * 
 * @example
 * ```tsx
 * // Basic usage
 * <button onClick={() => copyToClipboard('Hello world')}>Copy</button>
 * 
 * // Custom messages
 * <button onClick={() => copyToClipboard(
 *   email, 
 *   'Email copied!', 
 *   'Could not copy email'
 * )}>
 *   Copy Email
 * </button>
 * 
 * // With async/await
 * const handleCopy = async () => {
 *   try {
 *     await copyToClipboard(apiKey)
 *     // Additional success handling
 *   } catch (error) {
 *     // Additional error handling
 *   }
 * }
 * ```
 */
export async function copyToClipboard(
  text: string,
  successMessage: string = 'Copied to clipboard',
  errorMessage: string = 'Failed to copy to clipboard'
): Promise<void> {
  try {
    await navigator.clipboard.writeText(text)
    butterToast.success(successMessage)
  } catch (error) {
    console.error('Failed to copy text:', error)
    butterToast.error(errorMessage)
    throw error // Re-throw so caller can handle if needed
  }
}

/**
 * Copy text to clipboard without toast feedback
 * 
 * A silent version of copyToClipboard that doesn't show any toast notifications.
 * Useful when you want to handle the feedback yourself or when multiple copies
 * might happen in quick succession.
 * 
 * @param text - The text to copy to clipboard
 * @returns Promise that resolves to true on success, false on failure
 * 
 * @example
 * ```tsx
 * const handleCopy = async () => {
 *   const success = await copyToClipboardSilent(text)
 *   if (success) {
 *     setIsCopied(true)
 *     setTimeout(() => setIsCopied(false), 2000)
 *   }
 * }
 * ```
 */
export async function copyToClipboardSilent(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch (error) {
    console.error('Failed to copy text:', error)
    return false
  }
}

/**
 * Check if the Clipboard API is available
 * 
 * Useful for progressive enhancement - check before showing copy buttons
 * in environments where the Clipboard API might not be available.
 * 
 * @returns true if clipboard API is available, false otherwise
 * 
 * @example
 * ```tsx
 * {isClipboardAvailable() && (
 *   <button onClick={() => copyToClipboard(text)}>
 *     Copy
 *   </button>
 * )}
 * ```
 */
export function isClipboardAvailable(): boolean {
  return !!(navigator?.clipboard?.writeText)
}
